// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a card component that displays a button to view an organization's
 * audit log in a drawer. The audit log itself is loaded asynchronously when the drawer
 * is opened.
 */
import { OrganizationAuditList } from '@/account/components/organization-audit-list';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  CloseRounded,
  NavigateNextRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Suspense } from 'react';

interface Props {
  /**
   * The key of the organization whose audit events should be displayed.
   */
  organizationKey: string;
}

/**
 * A card component that provides access to an organization's audit log through
 * a drawer interface. The audit log is loaded asynchronously when the drawer
 * is opened.
 *
 * The component shows a simple card with a "View audit log" button. When clicked,
 * it opens a drawer from the right side of the screen containing the full audit log.
 */
export function OrganizationAuditCard(props: Props) {
  const theme = useTheme();
  const drawer = useDisclosure();

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Drawer
        anchor="right"
        open={drawer.isOpen}
        onClose={drawer.close}
        PaperProps={{ sx: { maxWidth: '35rem', minWidth: '25rem' } }}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <Stack
          spacing={1}
          padding={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1">Audit log</Typography>
          <IconButton size="small" onClick={drawer.close}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Box padding={2}>
          <SupportErrorBoundary message="An error occurred while loading the audit log.">
            <Suspense>
              <OrganizationAuditList organizationKey={props.organizationKey} />
            </Suspense>
          </SupportErrorBoundary>
        </Box>
      </Drawer>
      <Stack
        useFlexGap
        spacing={1}
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
      >
        <Typography variant="overline" marginRight="auto">
          History
        </Typography>
        <Button
          type="button"
          size="small"
          onClick={drawer.open}
          endIcon={<NavigateNextRounded fontSize="small" />}
        >
          View audit log
        </Button>
      </Stack>
    </Paper>
  );
}
